import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Tooltip from "../../../../../../../Components/Tooltip/Tooltip";
import {
  FiShield,
  FiLock,
  FiUsers,
  FiHome,
  FiGrid,
  FiClock,
  FiGift,
  FiMail,
  FiSun,
  FiMaximize2,
  FiArchive,
} from "react-icons/fi";

const PersonIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const buildShipperSideBar = (isOwner) => [
  {
    heading: "ROLES & PERMISSIONS",
    i18nKey: "sidebar.headings.rolesPermissions",
    menus: [
      {
        name: "Manage Roles",
        i18nKey: "sidebar.items.manageRoles",
        url: "/settings/manage-roles",
        icon: FiShield,
      },
      // Manage Permissions hidden for now
      // { name: "Manage Permissions", url: "/settings/manage-permissions", icon: FiLock },
    ],
    hidden: !isOwner,
  },
  {
    heading: "HELPER USERS",
    i18nKey: "sidebar.headings.helperUsers",
    menus: [
      {
        name: "Manage Helper Users",
        i18nKey: "sidebar.items.manageHelperUsers",
        url: "/settings/manage-helper-users",
        icon: FiUsers,
      },
    ],
    hidden: !isOwner,
  },
  {
    heading: "WAREHOUSE",
    i18nKey: "sidebar.headings.warehouse",
    menus: [
      {
        name: "Manage Warehouse",
        i18nKey: "sidebar.items.manageWarehouse",
        url: "/settings/manage-warehouse",
        icon: FiHome,
      },
    ],
    hidden: !isOwner,
  },
  {
    heading: "GENERAL SETTINGS",
    i18nKey: "sidebar.headings.general",
    menus: [
      {
        name: "Table Display & Pagination",
        i18nKey: "sidebar.items.tablePagination",
        url: "/settings/table-pagination",
        icon: FiGrid,
      },
      {
        name: "Default Time Zone",
        i18nKey: "sidebar.items.timeZone",
        url: "/settings/time-zone",
        icon: FiClock,
      },
      // { name: "Unit of Measurement", url: "/settings/units", icon: FiMaximize2 },
      // The account owner's alone: helpers don't get it at all.
      ...(isOwner
        ? [
            {
              name: "Customer Delivery Emails",
              i18nKey: "sidebar.items.deliveryEmails",
              url: "/settings/delivery-emails",
              icon: FiMail,
            },
            {
              name: "Auto-archive Orders",
              i18nKey: "sidebar.items.autoArchive",
              url: "/settings/auto-archive",
              icon: FiArchive,
            },
          ]
        : []),
    ],
  },
  {
    heading: "APPEARANCE",
    i18nKey: "sidebar.headings.appearance",
    menus: [
      {
        name: "Theme",
        i18nKey: "sidebar.items.theme",
        url: "/settings/appearance",
        icon: FiSun,
      },
    ],
  },
  // Kept last: settings pages pass hard-coded "section_item" indices to
  // <SideBar active>, and those pages are shared with the other roles.
  // Referrals belong to the parent account only, so helpers don't get the
  // section at all, not even greyed out.
  ...(isOwner
    ? [
        {
          heading: "REFERRALS",
          i18nKey: "sidebar.headings.referrals",
          menus: [
            {
              name: "Manage Referrals",
              i18nKey: "sidebar.items.manageReferrals",
              url: "/settings/referrals",
              icon: FiGift,
            },
          ],
        },
      ]
    : []),
];

const buildWarehouseSideBar = (isOwner) => [
  {
    heading: "ROLES & PERMISSIONS",
    i18nKey: "sidebar.headings.rolesPermissions",
    menus: [
      {
        name: "Manage Roles",
        i18nKey: "sidebar.items.manageRoles",
        url: "/warehouse/settings/manage-roles",
        icon: FiShield,
      },
      // Manage Permissions hidden for now
      // { name: "Manage Permissions", url: "/warehouse/settings/manage-permissions", icon: FiLock },
    ],
    hidden: !isOwner,
  },
  {
    heading: "HELPER USERS",
    i18nKey: "sidebar.headings.helperUsers",
    menus: [
      {
        name: "Manage Helper Users",
        i18nKey: "sidebar.items.manageHelperUsers",
        url: "/warehouse/settings/manage-helper-users",
        icon: FiUsers,
      },
    ],
    hidden: !isOwner,
  },
  {
    heading: "WAREHOUSE",
    i18nKey: "sidebar.headings.warehouse",
    menus: [
      {
        name: "Manage Warehouse",
        i18nKey: "sidebar.items.manageWarehouse",
        url: "/settings/manage-warehouse",
        icon: FiHome,
      },
    ],
    hidden: true,
  },
  {
    heading: "GENERAL SETTINGS",
    i18nKey: "sidebar.headings.general",
    menus: [
      {
        name: "Table Display & Pagination",
        i18nKey: "sidebar.items.tablePagination",
        url: "/warehouse/settings/table-pagination",
        icon: FiGrid,
      },
      {
        name: "Default Time Zone",
        i18nKey: "sidebar.items.timeZone",
        url: "/warehouse/settings/time-zone",
        icon: FiClock,
      },
      {
        name: "Email Configuration",
        i18nKey: "sidebar.items.emailConfig",
        url: "/warehouse/settings/email-config",
        icon: FiMail,
      },
    ],
  },
  {
    heading: "APPEARANCE",
    i18nKey: "sidebar.headings.appearance",
    menus: [
      {
        name: "Theme",
        i18nKey: "sidebar.items.theme",
        url: "/warehouse/settings/appearance",
        icon: FiSun,
      },
    ],
  },
];

const adminSideBar = [
  {
    heading: "ROLES & PERMISSIONS",
    i18nKey: "sidebar.headings.rolesPermissions",
    menus: [
      {
        name: "Manage Roles",
        i18nKey: "sidebar.items.manageRoles",
        url: "/admin/settings/manage-roles",
        icon: FiShield,
      },
      {
        name: "Manage Permissions",
        i18nKey: "sidebar.items.managePermissions",
        url: "/admin/settings/manage-permissions",
        icon: FiLock,
      },
    ],
    hidden: true,
  },
  {
    heading: "HELPER USERS",
    i18nKey: "sidebar.headings.helperUsers",
    menus: [
      {
        name: "Manage Helper Users",
        i18nKey: "sidebar.items.manageHelperUsers",
        url: "/admin/settings/manage-helper-users",
        icon: FiUsers,
      },
    ],
    hidden: true,
  },
  {
    heading: "WAREHOUSE",
    i18nKey: "sidebar.headings.warehouse",
    menus: [
      {
        name: "Manage Warehouse",
        i18nKey: "sidebar.items.manageWarehouse",
        url: "/settings/manage-warehouse",
        icon: FiHome,
      },
    ],
    hidden: true,
  },
  {
    heading: "GENERAL SETTINGS",
    i18nKey: "sidebar.headings.general",
    menus: [
      {
        name: "Table Display & Pagination",
        i18nKey: "sidebar.items.tablePagination",
        url: "/admin/settings/table-pagination",
        icon: FiGrid,
      },
      {
        name: "Default Time Zone",
        i18nKey: "sidebar.items.timeZone",
        url: "/admin/settings/time-zone",
        icon: FiClock,
      },
    ],
  },
  {
    heading: "APPEARANCE",
    i18nKey: "sidebar.headings.appearance",
    menus: [
      {
        name: "Theme",
        i18nKey: "sidebar.items.theme",
        url: "/admin/settings/appearance",
        icon: FiSun,
      },
    ],
  },
];

const carrierSideBar = [
  {
    heading: "ROLES & PERMISSIONS",
    i18nKey: "sidebar.headings.rolesPermissions",
    menus: [
      {
        name: "Manage Roles",
        i18nKey: "sidebar.items.manageRoles",
        url: "/carrier/settings/manage-roles",
        icon: FiShield,
      },
      {
        name: "Manage Permissions",
        i18nKey: "sidebar.items.managePermissions",
        url: "/carrier/settings/manage-permissions",
        icon: FiLock,
      },
    ],
    hidden: true,
  },
  {
    heading: "HELPER USERS",
    i18nKey: "sidebar.headings.helperUsers",
    menus: [
      {
        name: "Manage Helper Users",
        i18nKey: "sidebar.items.manageHelperUsers",
        url: "/carrier/settings/manage-helper-users",
        icon: FiUsers,
      },
    ],
    hidden: true,
  },
  {
    heading: "WAREHOUSE",
    i18nKey: "sidebar.headings.warehouse",
    menus: [
      {
        name: "Manage Warehouse",
        i18nKey: "sidebar.items.manageWarehouse",
        url: "/settings/manage-warehouse",
        icon: FiHome,
      },
    ],
    hidden: true,
  },
  {
    heading: "GENERAL SETTINGS",
    i18nKey: "sidebar.headings.general",
    menus: [
      {
        name: "Table Display & Pagination",
        i18nKey: "sidebar.items.tablePagination",
        url: "/carrier/settings/table-pagination",
        icon: FiGrid,
      },
    ],
  },
  {
    heading: "APPEARANCE",
    i18nKey: "sidebar.headings.appearance",
    menus: [
      {
        name: "Theme",
        i18nKey: "sidebar.items.theme",
        url: "/carrier/settings/appearance",
        icon: FiSun,
      },
    ],
  },
];

const shipperUserSiderBar = [
  {
    heading: "ROLES & PERMISSIONS",
    i18nKey: "sidebar.headings.rolesPermissions",
    menus: [
      {
        name: "Manage Roles",
        i18nKey: "sidebar.items.manageRoles",
        url: "/carrier/settings/manage-roles",
        icon: FiShield,
      },
      {
        name: "Manage Permissions",
        i18nKey: "sidebar.items.managePermissions",
        url: "/carrier/settings/manage-permissions",
        icon: FiLock,
      },
    ],
    hidden: true,
  },
  {
    heading: "HELPER USERS",
    i18nKey: "sidebar.headings.helperUsers",
    menus: [
      {
        name: "Manage Helper Users",
        i18nKey: "sidebar.items.manageHelperUsers",
        url: "/carrier/settings/manage-helper-users",
        icon: FiUsers,
      },
    ],
    hidden: true,
  },
  {
    heading: "WAREHOUSE",
    i18nKey: "sidebar.headings.warehouse",
    menus: [
      {
        name: "Manage Warehouse",
        i18nKey: "sidebar.items.manageWarehouse",
        url: "/settings/manage-warehouse",
        icon: FiHome,
      },
    ],
    hidden: true,
  },
  {
    heading: "GENERAL SETTINGS",
    i18nKey: "sidebar.headings.general",
    menus: [
      {
        name: "Table Display & Pagination",
        i18nKey: "sidebar.items.tablePagination",
        url: "/carrier/settings/table-pagination",
        icon: FiGrid,
      },
    ],
    hidden: true,
  },
  {
    heading: "APPEARANCE",
    i18nKey: "sidebar.headings.appearance",
    menus: [
      {
        name: "Theme",
        i18nKey: "sidebar.items.theme",
        url: "/settings/appearance",
        icon: FiSun,
      },
    ],
  },
];

// Every role's navbar is the shared Navbar: an h-12 bar plus a 1px bottom
// border. The sidebar sits flush under it and fills exactly the rest of the
// screen, so it looks the same on every tab and never adds a page scrollbar.
const NAVBAR_HEIGHT = 49;

const SideBar = ({ active }) => {
  const { t } = useTranslation("settings");
  const navigate = useNavigate();
  const user = useSelector((state) => state.user);
  const [sideBarMenus, setSideBarMenus] = useState([]);

  useEffect(() => {
    const isWarehouseOwner = !user?.parent_id;
    const isShipperOwner =
      (user?.role === "Shipper" || user?.role === "oneTimeShipper") &&
      !user?.parent_id;
    setSideBarMenus(
      user.role === "Shipper" || user.role === "ShipperUser"
        ? buildShipperSideBar(isShipperOwner)
        : user.role === "warehouseUser"
          ? buildWarehouseSideBar(isWarehouseOwner)
          : user.role === "Carrier"
            ? carrierSideBar
            : user.role === "Admin"
              ? adminSideBar
              : shipperUserSiderBar,
    );
  }, []);

  // Spacing is in px and lists/paragraphs are reset explicitly: Bootstrap is
  // loaded globally, and its !important p-/m-/gap-0…5 utilities and reboot
  // margins would otherwise override the Tailwind classes of the same name.
  return (
    <aside
      className="thin__scrollbar sticky flex w-[264px] min-w-[264px] shrink-0 flex-col self-start overflow-y-auto border-r border-ui-border bg-ui-surface"
      style={{
        top: NAVBAR_HEIGHT,
        height: `calc(100vh - ${NAVBAR_HEIGHT}px)`,
      }}
    >
      <div className="border-b border-ui-border-subtle px-[20px] pb-[16px] pt-[20px]">
        <h2 className="m-0 font-jakarta text-[15px] font-semibold leading-tight tracking-tight text-ui-text">
          {t("sidebar.title")}
        </h2>
        <p className="mb-0 mt-1 text-xs text-ui-text-subtle">
          {t("sidebar.subtitle")}
        </p>
      </div>

      <nav
        aria-label={t("sidebar.title")}
        className="flex flex-col gap-[16px] px-[12px] pb-[24px] pt-[16px]"
      >
        {sideBarMenus.map(({ heading, i18nKey, menus, hidden }, index) => (
          <Tooltip title={hidden ? t("sidebar.comingSoon") : ""} key={index}>
            <div className={hidden ? "pointer-events-none opacity-50" : ""}>
              <p className="mb-1.5 px-2.5 text-[10px] font-semibold uppercase tracking-widest text-ui-text-subtle">
                {t(i18nKey, { defaultValue: heading })}
              </p>
              <ul className="m-0 flex list-none flex-col gap-px p-0">
                {menus.map(({ name, i18nKey: nameKey, url, icon: Icon }, mindex) => {
                  const isActive = active === `${index}_${mindex}`;
                  return (
                    <li key={mindex}>
                      <button
                        type="button"
                        disabled={hidden}
                        aria-current={isActive ? "page" : undefined}
                        onClick={() => navigate(url)}
                        className={`group relative flex w-full items-center gap-2.5 rounded-md px-2.5 py-[7px] text-left text-[13px] font-medium transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand ${
                          isActive
                            ? "bg-brand-light text-brand"
                            : "text-ui-text-muted hover:bg-ui-surface-sunken hover:text-brand"
                        }`}
                      >
                        {isActive && (
                          <span
                            aria-hidden="true"
                            className="absolute inset-y-1.5 left-0 w-[3px] rounded-r-full bg-brand"
                          />
                        )}
                        <span
                          className={`shrink-0 transition-colors duration-200 ${
                            isActive
                              ? "text-brand"
                              : "text-ui-text-subtle group-hover:text-brand"
                          }`}
                        >
                          {Icon ? <Icon className="h-4 w-4" /> : <PersonIcon />}
                        </span>
                        <span className="min-w-0 leading-snug">
                          {t(nameKey, { defaultValue: name })}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          </Tooltip>
        ))}
      </nav>
    </aside>
  );
};

export default SideBar;
